import { Localized } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Box,
    Skeleton,
    Stack,
    Avatar,
    Autocomplete,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes";
import { SaveButton } from "../../../../app/components/SaveButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { RequiredError } from "../../../../app/components/RequiredError";
import { CancelButton } from "../../../../app/components/CancelButton";
import { selectStructureNodeById } from "../../../device/structureSlice";
import { DocumentLanguages, DocumentTypes } from "../../../documents/documentTypes";
import { DocumentLanguageSelect } from "../../../documents/controls/DocumentLanguageSelect";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { alpha } from "@mui/material/styles";
import { CreationMode, DocumentToCreate, prepareValidationStage, PrepareValidationStagePayload, ProcessStageOverview } from "../validationProcessApi";
import { ClientDocumentTemplateSelectField } from "../../../documents/templates/ClientDocumentTemplateSelectField";
import { LoadUsers } from "../../../../app/AppDataLoader";
import PersonIcon from "@mui/icons-material/Person";
import { selectAllUsers } from "../../../users/usersSlice";
import { DocumentHeader } from "../../../documents/documentsApi";
import { DocumentSelectField } from "../../../documents/controls/DocumentSelectField";
import React from "react";

const fixedDocType: DocumentTypes = "RA";

type RiskAnalysisCreationOption = "ISHIKAWA" | "FMEA" | "FROM_URS";

type UserOption = {
    userName: string;
    firstname: string;
    surname: string;
    email: string;
    position?: string;
};


export interface RAStageActivationProps {
    stageCode: "RA";
    processId: AppId | null;
    structureId: number;
    processName?: string;
    processNumber?: string;
    deviceId?: string;

    onSuccess: (stage: ProcessStageOverview) => void;
    onCancel: () => void;
}

export const RAStageActivation: React.FC<RAStageActivationProps> = (props) => {
    const dispatch = useAppDispatch();
    const allUsers = useAppSelector(selectAllUsers);

    const users: UserOption[] = React.useMemo(
        () =>
            (allUsers ?? []).map((u: any) => ({
                userName: String(u.userName),
                firstname: u.firstName ?? u.firstname ?? "",
                surname: u.lastName ?? u.surname ?? "",
                email: u.email ?? "",
                position: u.position,
            })),
        [allUsers]
    );
useEffect(() => {
    alert("RAStageActivation opened");
}, []);
    const findUserByUsername = (username: string | null, list: UserOption[]) =>
        username ? list.find(u => u.userName === username) ?? null : null;

    const [language, setLanguage] = useState<DocumentLanguages>("EN");

    const [documentNumber, setDocumentNumber] = useState("");
    const [title, setTitle] = useState("");
    const [selectedOption, setSelectedOption] = useState<RiskAnalysisCreationOption>("ISHIKAWA");
    const [selectedUrsId, setSelectedUrsId] = useState<string | null>(null);
    const [selectedUrsDoc, setSelectedUrsDoc] = useState<DocumentHeader | null>(null);
    const [responsible, setResponsible] = useState<string | null>(null);
    const [responsibleDirty, setResponsibleDirty] = useState(false);

    const [creationMode, setCreationMode] = useState<CreationMode>("TEMPLATE");
    const [templateId, setTemplateId] = useState<string | "">("");

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const structure = useAppSelector((state) =>
        props.structureId ? selectStructureNodeById(state, props.structureId) : null
    );

    const [numberDirty, setNumberDirty] = useState(false);
    const [titleDirty, setTitleDirty] = useState(false);

    const isFromUrs = selectedOption === "FROM_URS";

    const isResponsibleOk = !!(responsible && responsible.trim() !== "");
    const isUrsOk = !isFromUrs || !!selectedUrsId;

    const isBasicsOk =
        !!documentNumber.trim() &&
        !!title.trim() &&
        !!language &&
        !!props.processId &&
        isResponsibleOk &&
        isUrsOk;

    const isTemplateOk =
        creationMode === "EMPTY"
            ? true
            : (templateId !== "" && Number(templateId) > 0);

    const isFormValid = isBasicsOk && isTemplateOk;

    const structureDisplay = useMemo(() => {
        const n = structure?.name ?? "";
        const nEN = structure?.name_EN ?? "";
        const nm = language === "EN" ? (nEN || n) : (n || nEN);
        const code = structure?.code ? ` (${structure.code})` : "";
        return (nm || structure?.code) ? `${nm}${code}` : "-";
    }, [structure?.name, structure?.name_EN, structure?.code, language]);

    // --- generatory
    const buildDefaultNumber = (type?: string, structCode?: string, lang?: string, date: Date = new Date()) => {
        if (!type || !lang) return "";
        const year = date.getFullYear();
        const t = String(type).toUpperCase();
        const sc = (structCode ? String(structCode) : "GEN").toUpperCase().replace(/\s+/g, "-");
        const l = String(lang).toUpperCase();
        return `${t}-${sc}-${year}-${l}`;
    };

    const buildDefaultTitle = (type?: string, langCode?: string, st?: { name?: string; name_EN?: string; code?: string }) => {
        if (!type || !langCode) return "";
        const lc = langCode.toUpperCase();
        const phrase = lc === "EN" ? "Template for" : "Szablon dla";
        const langWord = lc === "EN" ? "language" : "język";
        const deviceName = lc === "EN" ? (st?.name_EN ?? st?.name ?? "") : (st?.name ?? st?.name_EN ?? "");
        const devicePart = deviceName ? `"${deviceName}"${st?.code ? ` (${st.code})` : ""}` : "";
        const langPart = `${langWord} (${lc})`;
        return devicePart ? `${type} - ${phrase} - ${devicePart} - ${langPart}` : `${type} - ${langPart}`;
    };

    useEffect(() => {
        if (!numberDirty) {
            setDocumentNumber(buildDefaultNumber(String(fixedDocType), structure?.code, String(language)));
        }
        if (!titleDirty) {
            setTitle(buildDefaultTitle(String(fixedDocType), String(language), structure ?? undefined));
        }
    }, [fixedDocType, language, structure?.name, structure?.name_EN, structure?.code]);

    // --- handlery
    const handleTitleChange: AppTextFieldHandler = useCallback((e) => {
        setTitle(e.target.value);
        setTitleDirty(true);
    }, []);

    const handleDocumentNumberChange: AppTextFieldHandler = useCallback((e) => {
        setDocumentNumber(e.target.value);
        setNumberDirty(true);
    }, []);

    const handleModeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCreationMode(e.target.value as CreationMode);
    }, []);

    const handleAnalysisTypeChange = useCallback((_: React.MouseEvent<HTMLElement>, value: RiskAnalysisCreationOption | null) => {
        if (!value) return;

        setSelectedOption(value);
        if (value !== "FROM_URS") {
            setSelectedUrsId(null);
            setSelectedUrsDoc(null);
        }
    }, []);

    const handleSaveClick = async () => {
        if (!isFormValid) {
            setServerError("fill-all-required-fields");
            return;
        }

        // konwencja: gdy EMPTY -> templateId = 0
        const normalizedTemplateId =
            creationMode === "TEMPLATE" ? Number(templateId) : 0;

        const docs: DocumentToCreate[] = [
            {
                type: fixedDocType,
                number: documentNumber.trim(),
                title: title.trim(),
                language,
                responsible: responsible?.trim() || null,
                creationMode: creationMode,
                templateId: normalizedTemplateId,
            },
        ];

        const payload: PrepareValidationStagePayload = {
            stageCode: fixedDocType,
            processId: props.processId!,
            documents: docs,
            toLink: selectedUrsId ? [{ type: "URS", documentId: Number(selectedUrsId) }] : []
        };
        
        setSaving(true);
        setServerError("");
        try {
            const result = await dispatch(prepareValidationStage(payload)).unwrap();
            dispatch(showSuccess("saved"));
            props.onSuccess(result as ProcessStageOverview);
        } catch (e) {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open fullWidth maxWidth="md"
            slotProps={{
                backdrop: {
                    sx: {
                        bgcolor: (t) => alpha(t.palette.grey[900], 0.5),
                    },
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                <Localized id="ak-stage-activation-title">
                    <span>Przygotowanie dokumentu AK</span>
                </Localized>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <Box
                    sx={(t) => ({
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 1,
                        p: 1.25,
                        mb: 2,
                    })}
                >
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                        <Localized id="risk-analysis-select-type">
                            <span>_Jaką analizę ryzyka wygenerować?</span>
                        </Localized>
                    </Typography>

                    <ToggleButtonGroup
                        value={selectedOption}
                        exclusive
                        size="small"
                        onChange={handleAnalysisTypeChange}
                        fullWidth
                        sx={{
                            alignSelf: "flex-start",
                            "& .MuiToggleButton-root.Mui-selected": {
                                borderColor: "success.main",
                                borderWidth: 1.5,
                            },
                        }}
                    >
                        <ToggleButton value="ISHIKAWA">
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                {selectedOption === "ISHIKAWA" && <CheckCircleIcon fontSize="small" color="success" />}
                                <Typography variant="body2">Ishikawa</Typography>
                            </Box>
                        </ToggleButton>
                        <ToggleButton value="FMEA">
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                {selectedOption === "FMEA" && <CheckCircleIcon fontSize="small" color="success" />}
                                <Typography variant="body2">FMEA</Typography>
                            </Box>
                        </ToggleButton>
                        <ToggleButton value="FROM_URS">
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                {selectedOption === "FROM_URS" && <CheckCircleIcon fontSize="small" color="success" />}
                                <Typography variant="body2">From URS</Typography>
                            </Box>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* Sekcja opisowa */}
                <Box
                    sx={(t) => ({
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        p: 2,
                        mb: 2,
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 1.5,
                        bgcolor: t.palette.action.hover,
                    })}
                >
                    <InfoOutlinedIcon sx={{ mt: 1, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Dokument zostanie przygotowany dla:
                        </Typography>

                        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 0.5, columnGap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <AssignmentOutlinedIcon fontSize="small" />
                                <Box component="span" sx={{ fontWeight: 600 }}>Proces:</Box>
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                {(props.processName || "-")}{props.processNumber ? ` (${props.processNumber})` : ""}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <DevicesOtherOutlinedIcon fontSize="small" />
                                <Box component="span" sx={{ fontWeight: 600 }}>Urządzenie:</Box>
                            </Typography>
                            <Typography variant="body2">
                                {props.deviceId || "-"}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <ApartmentOutlinedIcon fontSize="small" />
                                <Box component="span" sx={{ fontWeight: 600 }}>Struktura:</Box>
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                {structureDisplay}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* 1) Numer + Język (język obok numeru) */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12} md={8}>
                        <TextField
                            helperText={documentNumber === "" && <RequiredError />}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={documentNumber}
                            error={documentNumber === ""}
                            inputProps={{ maxLength: 50 }}
                            onChange={handleDocumentNumberChange}
                            label={<Localized id="document-number"><span>_Numer dokumentu</span></Localized>}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DocumentLanguageSelect
                            defaultValue={language}
                            required
                        />
                    </Grid>
                </Grid>

                {/* 2) Tytuł (pod numerem) */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            helperText={title === "" && <RequiredError />}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={title}
                            error={title === ""}
                            onChange={handleTitleChange}
                            label={<Localized id="document-title"><span>_Tytuł</span></Localized>}
                        />
                    </Grid>
                </Grid>

                {isFromUrs && (
                    <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                        <Grid item xs={12}>
                            <DocumentSelectField
                                documentType="URS"
                                value={selectedUrsId}
                                selected={selectedUrsDoc}
                                filterStructureId={props.structureId != null ? String(props.structureId) : null}
                                defaultStatusFilter={["A", "P"]}
                                reserveHelperSpace={false}
                                onChange={(id, doc) => {
                                    setSelectedUrsId(id);
                                    setSelectedUrsDoc(doc ?? null);
                                }}
                            />
                            {!isUrsOk && <FormHelperText error><RequiredError /></FormHelperText>}
                        </Grid>
                    </Grid>
                )}

                {/* 3) Osoba odpowiedzialna (pod tytułem) */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                    <Grid item xs={12}>
                        <LoadUsers component={<Skeleton variant="rectangular" height={180} />}>
                            <Autocomplete<UserOption, false, false, false>
                                options={users}
                                onChange={(_, u) => {
                                    setResponsible(u?.userName ?? null);
                                    setResponsibleDirty(true);
                                }}
                                onBlur={() => setResponsibleDirty(true)}
                                value={findUserByUsername(responsible, users)}
                                isOptionEqualToValue={(o, v) => o.userName === v.userName}
                                getOptionLabel={(u) => (u ? `${u.firstname} ${u.surname} (${u.email})` : "")}
                                renderOption={(props, u) => (
                                    <li {...props} key={u.userName}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar
                                                sx={{ width: 28, height: 28, fontSize: 12, lineHeight: 1 }}
                                                src={(u as any).avatarUrl}
                                            >
                                                {<PersonIcon sx={{ fontSize: 16 }} />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {u.firstname} {u.surname}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {u.email}{u.position ? ` · ${u.position}` : ""}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        label="Osoba odpowiedzialna za przygotowanie dokumentu"
                                        placeholder="Wybierz osobę"
                                        required
                                        error={responsibleDirty && !isResponsibleOk}
                                        helperText={responsibleDirty && !isResponsibleOk ? <RequiredError /> : null} />
                                )}
                            />
                        </LoadUsers>
                    </Grid>
                </Grid>

                {/* 4) Tryb tworzenia (na końcu) */}
                <Box
                    sx={(t) => ({
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 1,
                        p: 1.25,
                        mt: 1,
                    })}
                >
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                        <Localized id="creation-mode"><span>_Tryb tworzenia</span></Localized>
                    </Typography>
                    <RadioGroup row value={creationMode} onChange={handleModeChange}>
                        <FormControlLabel
                            value="EMPTY"
                            control={<Radio size="small" />}
                            sx={{ mr: 2 }}
                            label={<Typography variant="body2">Pusty</Typography>}
                        />
                        <FormControlLabel
                            value="TEMPLATE"
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">Z szablonu</Typography>}
                        />
                    </RadioGroup>

                    {creationMode === "TEMPLATE" && (
                        <Box sx={{ mt: 1 }}>
                            <ClientDocumentTemplateSelectField
                                structureId={props.structureId}
                                documentType={props.stageCode}
                                value={templateId}
                                onChange={(id) => setTemplateId(id)}
                            />
                        </Box>
                    )}
                </Box>

                {serverError && (
                    <Alert sx={{ mt: 2 }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}><span>Server error</span></Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog >
    );
};
